import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext();
const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour in ms

// Enable cookies for all axios requests (HttpOnly auth cookie)
axios.defaults.withCredentials = true;

// Axios interceptor: add Authorization header only when token exists in memory
let _inMemoryToken = null;
axios.interceptors.request.use((config) => {
  if (_inMemoryToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${_inMemoryToken}`;
  }
  return config;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null); // In-memory only, NOT in localStorage
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const logout = useCallback(async () => {
    // Clear server-side HttpOnly cookie
    try {
      await axios.post(`${API}/auth/logout`);
    } catch {
      // Ignore errors during logout
    }
    _inMemoryToken = null;
    setToken(null);
    setUser(null);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  // Inactivity auto-logout
  const resetInactivityTimer = useCallback(() => {
    if (!user && !token) return;
    lastActivityRef.current = Date.now();
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      toast.warning('Sesión cerrada por inactividad (1 hora)');
      logout();
    }, INACTIVITY_TIMEOUT);
  }, [user, token, logout]);

  // Check auth on load via HttpOnly cookie
  useEffect(() => {
    // The HttpOnly cookie is sent automatically with withCredentials
    axios.get(`${API}/auth/me`)
      .then(res => { setUser(res.data); resetInactivityTimer(); })
      .catch(() => { setUser(null); setToken(null); })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for user activity
  useEffect(() => {
    if (!user) return;
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    const handler = () => resetInactivityTimer();
    events.forEach(e => window.addEventListener(e, handler, { passive: true }));
    return () => events.forEach(e => window.removeEventListener(e, handler));
  }, [user, resetInactivityTimer]);

  const login = async (email, password, captcha_token) => {
    const res = await axios.post(`${API}/auth/login`, { email, password, captcha_token });
    // Check if 2FA is required
    if (res.data.requires_2fa) {
      return res.data; // { requires_2fa, temp_token, device_name, email_hint }
    }
    // Server sets HttpOnly cookie. Token in body is for backward compat only.
    _inMemoryToken = res.data.token;
    setToken(res.data.token);
    setUser(res.data.user);
    resetInactivityTimer();
    return res.data;
  };

  const verify2FA = async (tempToken, code) => {
    const res = await axios.post(`${API}/auth/verify-2fa`, { temp_token: tempToken, code });
    // Server sets HttpOnly cookie
    _inMemoryToken = res.data.token;
    setToken(res.data.token);
    setUser(res.data.user);
    resetInactivityTimer();
    return res.data;
  };

  const register = async (email, password, company_name, phone, captcha_token, is_company, fiscal_data) => {
    const preferred_language = (localStorage.getItem('app_language') || 'es').split('-')[0];
    const res = await axios.post(`${API}/auth/register`, { email, password, company_name, phone, captcha_token, is_company, fiscal_data, preferred_language });
    // Server sets HttpOnly cookie
    _inMemoryToken = res.data.token;
    setToken(res.data.token);
    setUser(res.data.user);
    resetInactivityTimer();
    // Google Ads: track registration conversion
    import('../utils/googleAds.js').then(m => m.trackRegistration()).catch(() => {});
    return res.data;
  };

  const refreshUser = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setUser(res.data);
    } catch {}
  };

  const isAuthenticated = !!user;
  const isAdmin = user?.is_admin || false;

  return (
    <AuthContext.Provider value={{ user, token, loading, isAuthenticated, isAdmin, login, verify2FA, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
